/**
 * @typedef {import('./types/Agent').default} Agent
 * @typedef {import('./types/Agent').AgentRequest} AgentRequest
 * @typedef {Window & typeof globalThis & { dataLayer?: unknown[] }} GtagWindow
 */

import './App.css';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ErrorMessage from './components/ErrorMessage';
import Loading from './components/Loading';
import occupations from '@mooveguru/yhh-shared-config/users/agent/occupations.json';
import Service from './Service';
import ValuationPage from './views/ValuationPage';

function App() {
	const service = new Service();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const agentId = searchParams.get('id');
	const gtmId = searchParams.get('gtm_id');
	const tenant = searchParams.get('tenant');
	const [agent, setAgent] = useState(/** @type {Agent | null} */ (null));
	const [error, setError] = useState(/** @type {string | null} */ (null));
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!hasValidParams()) {
			setIsLoading(false);
			setError('Must have a valid tenant and agentID');
			return;
		}

		setAgentData();
	}, [agentId]);

	useEffect(() => {
		/** @type {HTMLScriptElement | undefined} */
		let script;

		if (gtmId) {
			script = document.createElement('script');
			script.src = 'https://www.googletagmanager.com/gtag/js';
			script.async = true;
			script.onload = () => {
				const gtagWindow = /** @type {GtagWindow} */ (window);

				gtagWindow.dataLayer = gtagWindow.dataLayer || [];

				/**
				 * @param {...unknown} args
				 * @returns {void}
				 */
				function gtag(...args) {
					gtagWindow.dataLayer?.push(args);
				}

				gtag('js', new Date());
				gtag('config', gtmId);
			};

			document.head.appendChild(script);
		}

		return () => {
			if (script) {
				document.head.removeChild(script);
			}
		};
	}, [gtmId]);

	function hasValidParams() {
		return Boolean(tenant && agentId);
	}

	async function setAgentData() {
		try {
			const agentData = await getAgent();
			const appTitle = await setTheme(agentData);

			setAgent({
				...agentData,
				appTitle,
			});
			setIsLoading(false);
			setError(null);
		} catch (caughtError) {
			setIsLoading(false);
			setError('Agent not found.');
		}
	}

	async function getAgent() {
		const response = await service.getAgentById(agentId);

		return mapAgent(response);
	}

	/**
	 * @param {AgentRequest} agentData
	 * @returns {Agent}
	 */
	function mapAgent(agentData) {
		const occupationKey = agentData.occupation;

		return {
			appTitle: null,
			emailAddress: agentData.email_address,
			group: resolveGroup(agentData.groups),
			headshot: agentData.headshot ?? null,
			id: agentId,
			name: getFullName(agentData.name),
			occupation:
				occupationKey && occupationKey in occupations
					? occupations[
							/** @type {keyof typeof occupations} */ (
								occupationKey
							)
					  ]
					: 'Loan Officer',
			slug: agentData.slug,
		};
	}

	/**
	 * @param {AgentRequest['groups']} groupList
	 * @returns {Agent['group']}
	 */
	function resolveGroup(groupList) {
		const primaryGroup =
			groupList.find((group) => group.is_primary) ?? null;

		if (!primaryGroup) {
			return null;
		}

		return {
			id: primaryGroup.id,
			isPrimary: primaryGroup.is_primary,
			name: primaryGroup.name,
		};
	}

	/**
	 * @param {Agent} agentData
	 * @returns {Promise<string | null>}
	 */
	async function setTheme(agentData) {
		const [themeResult, domainThemeResult] = await Promise.allSettled([
			service.getThemeBySlug(agentData.slug),
			service.getThemeBySlug('domain'),
		]);
		const themeResponse =
			themeResult.status === 'fulfilled' ? themeResult.value : null;
		const domainThemeResponse =
			domainThemeResult.status === 'fulfilled'
				? domainThemeResult.value
				: null;

		const themeColor = themeResponse?.colors.background ?? null;
		const themeColorDark = themeResponse?.colors.primary ?? null;

		if (themeColor) {
			document.documentElement.style.setProperty(
				'--theme-color',
				themeColor
			);
		}

		if (themeColorDark) {
			document.documentElement.style.setProperty(
				'--theme-color-dark',
				themeColorDark
			);
		}

		return (
			themeResponse?.appTitle ??
			domainThemeResponse?.appTitle ??
			'Quantarium Alternate'
		);
	}

	/**
	 * @param {AgentRequest['name']} name
	 * @returns {string}
	 */
	function getFullName(name) {
		return `${name?.first} ${name?.last}`.trim();
	}

	if (isLoading) {
		return <Loading />;
	}

	if (error) {
		return <ErrorMessage heading="Error" message={error} />;
	}

	return agent ? <ValuationPage agent={agent} /> : null;
}

export default App;
